import React, { useCallback } from 'react';
import { useSend } from '@sc1/common/hooks';
import {
  fromNow,
  useSearchParam,
  IChain,
  useStrategySubgraph,
  HedgedLP,
  usePriceOffsetSlippage,
} from '@sc1/common';
import { Box, Card, Text, Heading, Row } from '../core';
import { StatusButton } from '../StatusButton';
import type { CardProps } from 'theme-ui';
interface RebalanceStrategyProps extends CardProps {
  strategy: HedgedLP;
  disabled: boolean;
}

export const RebalanceStrategy = ({
  strategy,
  disabled,
  sx,
  ...rest
}: RebalanceStrategyProps): JSX.Element => {
  const { sendTx, txStatus } = useSend();

  const chain = useSearchParam().get('chain', 'all chains') as IChain;
  const lastRebalance = useStrategySubgraph(chain, strategy?.address)?.data
    ?.strategy?.lastRebalance;

  const { slippage, slippageDescription } = usePriceOffsetSlippage(strategy);

  const rebalanceStrategy = useCallback(() => {
    if (!strategy) return;
    slippage && sendTx(strategy, 'rebalance', [slippage]);
  }, [strategy, slippage, sendTx]);

  return (
    <Card
      sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end', ...sx }}
      {...rest}
    >
      <Text variant="hint" mb={1}>
        Last Rebalanced {fromNow(Number(lastRebalance) * 1000)}
      </Text>
      <Heading mb={2} as="h3">
        Rebalance Strategy
      </Heading>
      <Row variant="collapse">
        <StatusButton
          disabled={disabled}
          onClick={rebalanceStrategy}
          status={txStatus}
        >
          Rebalance
        </StatusButton>
      </Row>
      <Box variant="text.info" mt={1}>
        {slippageDescription}
      </Box>
    </Card>
  );
};
